/* eslint-disable react-hooks/immutability --
   Falso positivo del linter su window.confirm() chiamato dentro callback.
   Il modal è side-effect intenzionale + non muta stato React. */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import AppShell from "@/components/AppShell";
import DismissibleIntro from "@/components/DismissibleIntro";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  PaperPlaneTilt, Sparkle, ChatCircleDots, Trash, Plus,
  Spinner, ArrowDown, Lightbulb, Microphone, MicrophoneSlash,
} from "@phosphor-icons/react";

/**
 * Assistente — chat conversazionale con "Pronto", consulente immobiliare
 * (Claude Sonnet 4.5 via Emergent LLM Key).
 *
 * Layout:
 *  - Sidebar conversazioni (sempre visibile desktop, drawer mobile)
 *  - Area chat: lista messaggi + input ancorato in basso
 *  - Empty state con chip di suggerimenti
 */
// iter v29 r233 — Estratti `confirm()` esterni al componente per evitare
// false positive di ESLint react-hooks/immutability su window.confirm dentro
// callback. Comportamento identico, solo location diversa.
function _confirm(msg) {
  if (typeof window !== "undefined" && typeof window.confirm === "function") {
    return window.confirm(msg);
  }
  return false;
}

// iter v29 r233 — Assistente con supporto ?q= per auto-invio domanda dalle
// guide di /dashboard/aiuto. Aggiunta useSearchParams + autoSentRef per
// pulire la query string ed evitare auto-rinvio su refresh.

export default function Assistente() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // iter 242d: input vocale via Web Speech API (no chiamata backend extra,
  // sfrutta il riconoscimento del browser → 0 latenza e 0 costi).
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // iter v32 r30 — BUGFIX user: «mi ripete le parole, allora → allora allora
  // domani». Causa: l'onresult faceva `base = input_corrente` e ci
  // concatenava interim, ma input_corrente conteneva GIÀ il vecchio interim
  // → accumulo. Soluzione: salvare il testo PRE-registrazione in un ref e
  // sovrascrivere SEMPRE da quel punto in avanti.
  const recordingBaseRef = useRef("");

  // Init Web Speech API una volta
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = "it-IT";
    r.continuous = false;
    r.interimResults = true;
    r.onresult = (e) => {
      let interim = "";
      let final = "";
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      const transcript = (final + " " + interim).replace(/\s+/g, " ").trim();
      const base = recordingBaseRef.current;
      setInput(base ? `${base} ${transcript}`.trim() : transcript);
    };
    r.onend = () => {
      setRecording(false);
      // Salva il testo cumulato nel base così, se l'utente preme di nuovo il
      // microfono per continuare a parlare, le parole nuove si aggiungono in
      // coda invece di sovrascriversi a vicenda.
      recordingBaseRef.current = (inputRef.current?.value || "").trim();
    };
    r.onerror = () => setRecording(false);
    recognitionRef.current = r;
    return () => { try { r.abort(); } catch (_e) { /* */ } };
  }, []);

  const toggleRecording = () => {
    const r = recognitionRef.current;
    if (!r) {
      toast.error("Il tuo browser non supporta il riconoscimento vocale. Usa Chrome o Safari.");
      return;
    }
    if (recording) {
      try { r.stop(); } catch (_e) { /* */ }
      setRecording(false);
    } else {
      try {
        // iter v32 r30 — Salva il testo già scritto come "base immutabile"
        // per questa sessione di registrazione. Tutti gli interim/final
        // verranno appesi a questo base, senza sovrascriversi a vicenda.
        recordingBaseRef.current = (inputRef.current?.value || input || "").trim();
        r.start();
        setRecording(true);
      } catch (_e) {
        toast.error("Errore microfono. Verifica i permessi del browser.");
      }
    }
  };

  // iter v32 r30 — Textarea auto-resize: cresce fino a max-h-32 (8rem)
  // mentre l'utente scrive o detta. User: «la barra deve espandersi se no
  // non riesco a leggere quello che ho scritto».
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 128)}px`;
  }, [input]);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [convsR, sugR] = await Promise.all([
          api.get("/assistant/conversations"),
          api.get("/assistant/suggestions"),
        ]);
        setConvs(convsR.data.conversations || []);
        setSuggestions(sugR.data.suggestions || []);
      } catch (e) {
        // silent
      }
    })();
  }, []);

  // iter v29 r233 — Auto-invio se ?q= nella URL. Usato dalla pagina Aiuto
  // per aprire l'assistente con una domanda già scritta (es. "Come pubblico
  // il mio primo annuncio?") → l'utente non deve digitare nulla, vede
  // subito la risposta. Eseguito 1 sola volta per montaggio.
  //
  // iter v33 r74 · NUOVO: Se arriva via navigate() con location.state.prefetched
  // (nuovo flow Smart Mic con risposta gia' generata dal backend), mostra la
  // conversazione ISTANTANEAMENTE senza fare un'altra chiamata /assistant/ask.
  // Elimina ~5-10s di attesa quando l'utente fa una domanda a voce.
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (autoSentRef.current) return;
    // 1. Priority: risposta gia' pre-fetched via navigate state
    const pref = location.state?.prefetched;
    if (pref?.conversation_id && pref?.answer) {
      autoSentRef.current = true;
      setActiveConvId(pref.conversation_id);
      const msgs = [];
      if (pref.user_message) msgs.push(pref.user_message);
      msgs.push(pref.answer);
      setMessages(msgs);
      // Pulisci lo state cosi' un refresh non ripropone la stessa conversazione
      navigate(location.pathname, { replace: true, state: {} });
      // Refresh sidebar in background per vedere la nuova conversazione in lista
      api.get("/assistant/conversations").then(({ data }) => {
        setConvs(data.conversations || []);
      }).catch(() => {});
      return;
    }
    // 2. Fallback: ?q= nella URL (percorso storico da /aiuto o legacy voice)
    const q = searchParams.get("q");
    if (q && q.trim().length > 0) {
      autoSentRef.current = true;
      const text = q.trim();
      // Pulisci la query string subito così non si auto-rinvia su refresh
      const next = new URLSearchParams(searchParams);
      next.delete("q");
      setSearchParams(next, { replace: true });
      // Invia con piccolo delay per lasciare montare il layout
      setTimeout(() => { send(text); }, 250);
    }
  }, []);

  // Autoscroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const loadConversation = async (convId) => {
    setLoadingConv(true);
    setActiveConvId(convId);
    setSidebarOpen(false);
    try {
      const { data } = await api.get(`/assistant/conversations/${convId}`);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error("Impossibile caricare la conversazione");
    } finally {
      setLoadingConv(false);
    }
  };

  const newConversation = () => {
    setActiveConvId(null);
    setMessages([]);
    setSidebarOpen(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // iter v33 r108 · FIX A · Assistente router-aware
  // send() ora passa da /assistant/text-route (invece che /assistant/ask):
  //   - intent=chat  → answer già prefetched dal backend, 1 solo roundtrip
  //   - intent=agenda → mostra card preview con "Aggiungi in Agenda" / "Era una domanda"
  // Nessun redirect, nessun full page reload, nessun credito bruciato per dire
  // "vai in Agenda". L'utente resta nella stessa conversazione.
  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setInput("");
    setLoading(true);

    // Ottimistic message
    const optimistic = {
      id: `tmp_${Date.now()}`,
      role: "user",
      text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);

    try {
      const { data } = await api.post("/assistant/text-route", { text }, { timeout: 60000 });

      // ── Intent AGENDA → mostra preview inline con CTA (no salvataggio auto)
      if (data?.intent === "agenda" && data?.extracted) {
        const previewMsg = {
          id: `agpreview_${Date.now()}`,
          role: "assistant",
          kind: "agenda_preview",
          text: "",
          _extraction: { transcript: data.transcript || text, extracted: data.extracted },
          _original_text: text,
          created_at: new Date().toISOString(),
        };
        setMessages((m) => {
          const withoutTmp = m.filter((x) => x.id !== optimistic.id);
          return [
            ...withoutTmp,
            { ...optimistic, id: `usr_${Date.now()}`, saved: false },
            previewMsg,
          ];
        });
        return;
      }

      // ── Intent CHAT con prefetch → mostra risposta subito (1 solo roundtrip)
      if (data?.intent === "chat" && data?.answer && data?.user_message) {
        setMessages((m) => {
          const withoutTmp = m.filter((x) => x.id !== optimistic.id);
          return [...withoutTmp, data.user_message, data.answer];
        });
        if (!activeConvId && data.conversation_id) {
          setActiveConvId(data.conversation_id);
        }
        try {
          const { data: list } = await api.get("/assistant/conversations");
          setConvs(list.conversations || []);
        } catch (_e) { /* ignore */ }
        return;
      }

      // ── Fallback: prefetch fallito (LLM giù) → chiama /assistant/ask storico
      const askResp = await api.post("/assistant/ask", {
        conversation_id: activeConvId,
        message: text,
      });
      setMessages((m) => {
        const withoutTmp = m.filter((x) => x.id !== optimistic.id);
        return [...withoutTmp, askResp.data.user_message, askResp.data.answer];
      });
      if (!activeConvId) setActiveConvId(askResp.data.conversation_id);
      try {
        const { data: list } = await api.get("/assistant/conversations");
        setConvs(list.conversations || []);
      } catch (_e) { /* ignore */ }
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      toast.error(apiError(e, "Errore nella richiesta"));
      setInput(text); // restore so user can retry
    } finally {
      setLoading(false);
    }
  };

  // iter v33 r108 · FIX A · Aggiunge realmente in Agenda le entità estratte
  // dal router. Sostituisce la bubble preview con una bubble "✓ Aggiunto".
  const addToAgenda = async (msgId) => {
    const msg = messages.find((x) => x.id === msgId);
    if (!msg?._extraction) return;
    // Marca la card come "saving" per feedback immediato
    setMessages((m) => m.map((x) => x.id === msgId ? { ...x, _saving: true } : x));
    try {
      const { data } = await api.post("/agenda/apply", {
        transcript: msg._extraction.transcript,
        extracted: msg._extraction.extracted,
      });
      setMessages((m) => m.map((x) => x.id === msgId ? {
        ...x,
        kind: "agenda_saved",
        _saving: false,
        _memo: data,
      } : x));
      toast.success("Aggiunto in Agenda");
    } catch (e) {
      setMessages((m) => m.map((x) => x.id === msgId ? { ...x, _saving: false } : x));
      toast.error(apiError(e, "Errore salvataggio in Agenda"));
    }
  };

  // iter v33 r108 · FIX A · L'utente dice "era una domanda" → rimuove la
  // preview agenda e re-invia il testo a /assistant/ask (forzando chat).
  const wasQuestion = async (msgId) => {
    const msg = messages.find((x) => x.id === msgId);
    if (!msg?._original_text) return;
    setMessages((m) => m.filter((x) => x.id !== msgId));
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/ask", {
        conversation_id: activeConvId,
        message: msg._original_text,
      });
      setMessages((m) => [...m, data.answer]);
      if (!activeConvId) setActiveConvId(data.conversation_id);
      try {
        const { data: list } = await api.get("/assistant/conversations");
        setConvs(list.conversations || []);
      } catch (_e) { /* ignore */ }
    } catch (e) {
      toast.error(apiError(e, "Errore nella richiesta"));
    } finally {
      setLoading(false);
    }
  };

  const deleteConv = async (convId) => {
    if (!_confirm("Cancellare questa conversazione?")) return;
    try {
      await api.delete(`/assistant/conversations/${convId}`);
      setConvs((c) => c.filter((x) => x.id !== convId));
      if (activeConvId === convId) {
        setActiveConvId(null);
        setMessages([]);
      }
      toast.success("Conversazione cancellata");
    } catch (e) {
      toast.error("Errore cancellazione");
    }
  };

  const deleteAllConvs = async () => {
    if (!_confirm("Cancellare TUTTE le conversazioni? L'azione non è reversibile.")) return;
    try {
      const { data } = await api.delete("/assistant/conversations");
      setConvs([]);
      setActiveConvId(null);
      setMessages([]);
      toast.success(`${data.deleted_conversations} conversazioni cancellate`);
    } catch (e) {
      toast.error("Errore pulizia");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <AppShell title="Assistente" breadcrumb="Assistente" action={<ReopenGuidesBtn keys={["assistente:cosa-faccio"]} testId="assistente-reopen-guides" />}>
      <div className="flex h-[calc(100vh-9rem)] lg:h-[calc(100vh-7rem)] -m-4 lg:-m-6 bg-[#FAF9F5]">
        {/* Sidebar conversazioni */}
        <aside
          className={`${sidebarOpen ? "fixed inset-0 z-40 lg:static" : "hidden"} lg:block lg:relative lg:w-72 lg:flex-shrink-0 lg:bg-white lg:border-r lg:border-[#E8E5DE]`}
        >
          {/* Backdrop mobile */}
          {sidebarOpen && (
            <div className="lg:hidden absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          )}
          <div className="absolute lg:relative left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white border-r border-[#E8E5DE] flex flex-col">
            <div className="p-4 border-b border-[#E8E5DE] space-y-2">
              <button
                onClick={newConversation}
                className="w-full bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[13px] font-semibold py-2.5 rounded-md flex items-center justify-center gap-2"
                data-testid="assistant-new-conv"
              >
                <Plus size={14} weight="bold" /> Nuova conversazione
              </button>
              {convs.length > 0 && (
                <button
                  onClick={deleteAllConvs}
                  className="w-full text-[11px] text-[#9A968B] hover:text-red-500 py-1 transition-colors flex items-center justify-center gap-1"
                  data-testid="assistant-delete-all"
                >
                  <Trash size={11} /> Cancella tutte ({convs.length})
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {convs.length === 0 && (
                <div className="text-center text-[11.5px] text-[#9A968B] py-8 px-4 leading-relaxed">
                  Nessuna conversazione. Inizia chiedendo qualcosa a Pronto.
                </div>
              )}
              {convs.map((c) => (
                <button
                  key={c.id}
                  onClick={() => loadConversation(c.id)}
                  className={`group w-full text-left px-3 py-2.5 rounded-md transition-colors mb-1 ${
                    activeConvId === c.id
                      ? "bg-[#7C8B6E]/10 border border-[#7C8B6E]/30"
                      : "hover:bg-[#F1F4ED] border border-transparent"
                  }`}
                  data-testid={`assistant-conv-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium text-[#1C1C1A] truncate">{c.title}</div>
                      {c.last_msg_preview && (
                        <div className="text-[10.5px] text-[#9A968B] truncate mt-0.5">{c.last_msg_preview}</div>
                      )}
                    </div>
                    <span
                      onClick={(e) => { e.stopPropagation(); deleteConv(c.id); }}
                      className="opacity-0 group-hover:opacity-100 text-[#9A968B] hover:text-red-500 transition-opacity p-1 -m-1"
                      data-testid={`assistant-del-conv-${c.id}`}
                    >
                      <Trash size={12} />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main chat */}
        <main className="flex-1 flex flex-col min-w-0 bg-[#FAF9F5]">
          {/* Mobile open sidebar btn */}
          <div className="lg:hidden border-b border-[#E8E5DE] bg-white px-4 py-2 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-[12px] text-[#5A584F] flex items-center gap-1.5"
              data-testid="assistant-open-sidebar"
            >
              <ChatCircleDots size={14} /> Conversazioni
            </button>
            <button onClick={newConversation} className="text-[12px] text-[#7C8B6E] font-semibold flex items-center gap-1">
              <Plus size={12} weight="bold" /> Nuova
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-6" data-testid="assistant-messages-scroll">
            {messages.length === 0 && !loadingConv && (
              <EmptyState suggestions={suggestions} onPick={(t) => send(t)} />
            )}
            {loadingConv && (
              <div className="flex items-center justify-center py-12">
                <Spinner size={20} className="animate-spin text-[#7C8B6E]" />
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                msg={m}
                onAddToAgenda={() => addToAgenda(m.id)}
                onWasQuestion={() => wasQuestion(m.id)}
              />
            ))}
            {loading && <TypingBubble />}
          </div>

          {/* Input */}
          <div className="border-t border-[#E8E5DE] bg-white p-3 sm:p-4">
            <div className="max-w-3xl mx-auto flex items-end gap-2">
              {/* iter 242d: input area più contrastata: bordo verde salvia
                  always-on (no solo focus) + placeholder più scuro */}
              <div className={`flex-1 relative flex items-end rounded-xl border-2 transition-colors bg-white ${recording ? "border-red-400 shadow-sm shadow-red-200" : "border-[#7C8B6E]/40 focus-within:border-[#7C8B6E]"}`}>
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={recording ? "Sto ascoltando…  parla pure" : "Chiedi qualcosa a Pronto…"}
                  disabled={loading}
                  className="flex-1 resize-none px-4 py-2.5 text-[14px] focus:outline-none bg-transparent max-h-32 leading-relaxed placeholder:text-[#7C8B6E]/70"
                  style={{ minHeight: 44 }}
                  data-testid="assistant-input"
                />
                {/* Mic toggle button */}
                <button
                  onClick={toggleRecording}
                  disabled={loading}
                  className={`mr-1 mb-1 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                    recording
                      ? "bg-red-500 text-white animate-pulse"
                      : "text-[#7C8B6E] hover:bg-[#F1F4ED]"
                  }`}
                  aria-label={recording ? "Stop registrazione" : "Inizia registrazione vocale"}
                  data-testid="assistant-mic"
                  type="button"
                >
                  {recording ? <MicrophoneSlash size={16} weight="fill" /> : <Microphone size={16} weight="fill" />}
                </button>
              </div>
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                className="bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-40 disabled:cursor-not-allowed text-white w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors"
                aria-label="Invia"
                data-testid="assistant-send"
              >
                {loading ? <Spinner size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}
              </button>
            </div>
            <p className="text-[10.5px] text-[#9A968B] text-center mt-2 max-w-3xl mx-auto">
              {recording ? (
                <span className="text-red-500 font-semibold">● Registrazione in corso · tocca il microfono per fermare</span>
              ) : (
                <>Pronto è un orientamento esperto, non un consulente legale o fiscale. Per casi delicati verifica con un professionista.</>
              )}
            </p>
          </div>
        </main>
      </div>
    </AppShell>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────
function EmptyState({ suggestions, onPick }) {
  return (
    <div className="max-w-2xl mx-auto py-6 lg:py-12">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7C8B6E] to-[#A8B89A] flex items-center justify-center">
          <Sparkle size={20} weight="fill" className="text-white" />
        </div>
        <div>
          <h2 className="text-[20px] font-serif text-[#1C1C1A] leading-tight">Ciao, sono Pronto.</h2>
          <p className="text-[12.5px] text-[#6B6A65]">Il tuo consulente immobiliare. Chiedimi qualunque cosa.</p>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-semibold text-[#9A968B] mb-3">
          <Lightbulb size={11} weight="fill" /> Prova a chiedere
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => onPick(s)}
              className="text-left px-3.5 py-2.5 bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] hover:shadow-sm rounded-lg text-[12.5px] text-[#3F3D38] leading-snug transition-all"
              data-testid={`assistant-suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <DismissibleIntro storageKey="assistente:cosa-faccio" align="left" className="p-4 bg-[#F1F4ED] border border-[#7C8B6E]/20 rounded-lg">
          <div className="flex items-start gap-2.5">
            <Microphone size={16} weight="fill" className="text-[#7C8B6E] shrink-0 mt-0.5" />
            <div>
              <div className="text-[12.5px] font-semibold text-[#1C1C1A]">Cosa posso fare per te</div>
              <ul className="text-[11.5px] text-[#5A584F] mt-1.5 space-y-1 leading-relaxed list-disc list-inside">
                <li>Rispondo su normativa (cedolare, IMU, riscaldamento, codice civile)</li>
                <li>Ti spiego come usare ogni funzione dell{"'"}app</li>
                <li>Ti aiuto a scrivere annunci, descrizioni, email ai clienti</li>
                <li>Ti suggerisco strategie per gestire trattative difficili</li>
              </ul>
            </div>
          </div>
        </DismissibleIntro>
      </div>
    </div>
  );
}

function MessageBubble({ msg, onAddToAgenda, onWasQuestion }) {
  const isUser = msg.role === "user";

  // iter v33 r108 · FIX A · Bubble speciale per intent=agenda con preview
  if (msg.kind === "agenda_preview" || msg.kind === "agenda_saved") {
    return (
      <AgendaPreviewBubble
        msg={msg}
        onAddToAgenda={onAddToAgenda}
        onWasQuestion={onWasQuestion}
      />
    );
  }

  return (
    <div className={`flex gap-2.5 mb-4 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#7C8B6E] to-[#A8B89A] flex items-center justify-center shrink-0 mt-1">
          <Sparkle size={12} weight="fill" className="text-white" />
        </div>
      )}
      <div
        className={`max-w-[80%] sm:max-w-[75%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap break-words ${
          isUser
            ? "bg-[#1C1C1A] text-white rounded-tr-md"
            : "bg-white text-[#1C1C1A] border border-[#E8E5DE] rounded-tl-md"
        }`}
        data-testid={`assistant-msg-${msg.role}`}
      >
        {renderMarkdownLite(msg.text)}
      </div>
    </div>
  );
}

// iter v33 r108 · FIX A · Card preview agenda con CTA "Aggiungi in Agenda"
// e secondaria "Era una domanda". Dopo il salvataggio diventa "✓ Aggiunto".
function AgendaPreviewBubble({ msg, onAddToAgenda, onWasQuestion }) {
  const isSaved = msg.kind === "agenda_saved";
  const isSaving = !!msg._saving;
  const ex = msg._extraction?.extracted || {};
  const actions = Array.isArray(ex.actions) ? ex.actions : [];
  const contacts = Array.isArray(ex.contacts) ? ex.contacts : [];
  const primary = actions[0] || null;
  const contactName = primary?.contact_ref || contacts[0]?.name || null;
  const summary = ex.summary || primary?.what || msg._extraction?.transcript || "Promemoria";

  return (
    <div className="flex gap-2.5 mb-4 justify-start" data-testid={`assistant-msg-${msg.kind}`}>
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#7C8B6E] to-[#A8B89A] flex items-center justify-center shrink-0 mt-1">
        <Sparkle size={12} weight="fill" className="text-white" />
      </div>
      <div className="max-w-[85%] sm:max-w-[75%] bg-white text-[#1C1C1A] border border-[#E8E5DE] rounded-2xl rounded-tl-md p-3.5">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7C8B6E] mb-1.5">
          {isSaved ? "Aggiunto in Agenda" : "Ho capito così"}
        </div>

        <div className="text-[13.5px] font-semibold text-[#1C1C1A] leading-snug">
          {primary?.what || summary}
        </div>

        {(primary?.due || contactName || primary?.location) && (
          <div className="mt-1 text-[12.5px] text-[#5A584F] leading-snug space-y-0.5">
            {primary?.due && <div>{primary.due}</div>}
            {contactName && <div>Con {contactName}</div>}
            {primary?.location && <div>{primary.location}</div>}
          </div>
        )}

        {actions.length > 1 && (
          <div className="mt-2 text-[11px] text-[#9A968B]">
            + altre {actions.length - 1} {actions.length - 1 === 1 ? "azione" : "azioni"}
          </div>
        )}

        {isSaved ? (
          <div className="mt-3 flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 text-[12px] text-[#5A6A4D] font-semibold"
              data-testid={`assistant-agenda-saved-${msg.id}`}
            >
              <span className="w-4 h-4 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center text-[10px] font-bold">✓</span>
              Aggiunto in Agenda
            </span>
            <a
              href="/dashboard/agenda"
              className="ml-auto text-[11.5px] text-[#7C8B6E] underline hover:text-[#6B7A5D]"
              data-testid={`assistant-agenda-open-${msg.id}`}
            >
              Apri Agenda
            </a>
          </div>
        ) : (
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <button
              onClick={onAddToAgenda}
              disabled={isSaving}
              className="flex-1 py-2 bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-60 text-white font-semibold rounded-md text-[13px] flex items-center justify-center gap-1.5 transition-colors"
              data-testid={`assistant-agenda-add-${msg.id}`}
            >
              {isSaving ? <Spinner size={13} className="animate-spin" /> : null}
              Aggiungi in Agenda
            </button>
            <button
              onClick={onWasQuestion}
              disabled={isSaving}
              className="py-2 px-3 border border-[#E8E5DE] hover:border-[#7C8B6E] text-[#5A584F] rounded-md text-[12.5px] transition-colors"
              data-testid={`assistant-agenda-not-${msg.id}`}
            >
              Era una domanda
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex gap-2.5 mb-4 justify-start">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#7C8B6E] to-[#A8B89A] flex items-center justify-center shrink-0 mt-1">
        <Sparkle size={12} weight="fill" className="text-white" />
      </div>
      <div className="bg-white border border-[#E8E5DE] rounded-2xl rounded-tl-md px-4 py-3 inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-bounce" style={{ animationDelay: "0ms" }}></span>
        <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-bounce" style={{ animationDelay: "150ms" }}></span>
        <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E] animate-bounce" style={{ animationDelay: "300ms" }}></span>
      </div>
    </div>
  );
}

// Minimal markdown: **bold** and *italic*, plus newlines.
// Keep small dependency-free so we don't pull react-markdown.
function renderMarkdownLite(text) {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const parts = [];
    let lastIdx = 0;
    const regex = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
    let m;
    while ((m = regex.exec(line)) !== null) {
      if (m.index > lastIdx) parts.push(line.slice(lastIdx, m.index));
      if (m[1]) parts.push(<strong key={`${i}-${m.index}`}>{m[1]}</strong>);
      else parts.push(<em key={`${i}-${m.index}`} className="italic">{m[2]}</em>);
      lastIdx = regex.lastIndex;
    }
    if (lastIdx < line.length) parts.push(line.slice(lastIdx));
    return (
      <React.Fragment key={i}>
        {parts.length > 0 ? parts : line}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}
